/**
 * Initializes carousels.
 *
 * A carousel must have the general structure as follows:
 *  - Wrapper
 *    <div data-carousel='json with options'>...</div>
 *    + Plugins (optional):
 *      - data-carousel-class-name='json with ClassName options'
 *      - data-carousel-wheel-gesture='true' - WheelGesturesPlugin
 *      - data-carousel-dots - opt-in dot indicators
 *
 *  - Viewport
 *    <div data-carousel-viewport>...</div>
 *
 *  - Controls
 *    <div data-carousel-controls>...</div>
 *
 *  - Buttons:
 *    Inside the carousel controls div:
 *      - Previous button: <div data-carousel-prev>...</div>
 *      - Next button: <div data-carousel-next>...</div
 */

import EmblaCarousel from "embla-carousel"
import ClassNames from "embla-carousel-class-names"
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"

class Carousel {
  init() {
    const carousels = document.querySelectorAll('[data-carousel]')

    carousels.forEach((carousel) => {
      this.initCarousel(carousel)
    })
  }

  initCarousel(carousel) {
    const viewport = carousel.querySelector('[data-carousel-viewport]')

    if (!viewport) {
      console.error('Carousel viewport not found')
      return
    }

    const matchQuery = carousel.dataset.carouselMatchQuery || null
    let emblaApi = null
    let mediaQueryList = null
    let controlsBound = carousel.dataset.carouselControlsBound === 'true'

    const getPlugins = () => {
      const classNameOptions = carousel.dataset.carouselClassName
        ? JSON.parse(carousel.dataset.carouselClassName)
        : null
      const wheelGestureOptions = carousel.dataset.carouselWheelGesture ? true : false
      const plugins = []

      if (classNameOptions) {
        plugins.push(ClassNames(classNameOptions))
      }

      if (wheelGestureOptions) {
        plugins.push(WheelGesturesPlugin())
      }

      return plugins
    }

    const bindControls = () => {
      const controls = carousel.querySelector('[data-carousel-controls]')

      if (!controls) {
        return
      }

      const prevButton = controls.querySelector('[data-carousel-prev]')
      const nextButton = controls.querySelector('[data-carousel-next]')

      if (!prevButton || !nextButton) {
        return
      }

      if (!controlsBound) {
        prevButton.addEventListener('click', () => carousel._emblaApi?.scrollPrev())
        nextButton.addEventListener('click', () => carousel._emblaApi?.scrollNext())
        carousel.dataset.carouselControlsBound = 'true'
        controlsBound = true
      }

      const togglePrevNextBtnsState = () => {
        const api = carousel._emblaApi

        if (!api) {
          return
        }

        if (api.canScrollPrev()) {
          prevButton.removeAttribute('disabled')
        } else {
          prevButton.setAttribute('disabled', 'disabled')
        }

        if (api.canScrollNext()) {
          nextButton.removeAttribute('disabled')
        } else {
          nextButton.setAttribute('disabled', 'disabled')
        }
      }

      emblaApi
        .on('select', togglePrevNextBtnsState)
        .on('init', togglePrevNextBtnsState)
        .on('reInit', togglePrevNextBtnsState)

      togglePrevNextBtnsState()
    }

    const bindDots = () => {
      if (!carousel.hasAttribute('data-carousel-dots') || !emblaApi) {
        return
      }

      const dotsContainer = carousel.querySelector('[data-carousel-dots]')

      if (!dotsContainer) {
        return
      }

      dotsContainer.innerHTML = ''

      emblaApi.scrollSnapList().forEach((_, index) => {
        const dot = document.createElement('button')
        dot.type = 'button'
        dot.className = 'pdp-gallery__dot'
        dot.setAttribute('data-carousel-dot', '')
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`)
        dot.addEventListener('click', () => emblaApi.scrollTo(index))
        dotsContainer.appendChild(dot)
      })

      const updateDots = () => {
        const selected = emblaApi.selectedScrollSnap()

        dotsContainer.querySelectorAll('[data-carousel-dot]').forEach((dot, index) => {
          const isSelected = index === selected
          dot.setAttribute('aria-current', isSelected ? 'true' : 'false')
          dot.classList.toggle('is-selected', isSelected)
        })
      }

      emblaApi.on('select', updateDots).on('init', updateDots).on('reInit', updateDots)
      updateDots()
    }

    const setupEmbla = () => {
      if (emblaApi) {
        return
      }

      const options = carousel.dataset.carousel ? JSON.parse(carousel.dataset.carousel) : {}
      emblaApi = new EmblaCarousel(viewport, options, getPlugins())
      carousel._emblaApi = emblaApi

      if (carousel.id) {
        window.emblaApis[carousel.id] = emblaApi
      }

      bindControls()
      bindDots()

      if (carousel.id === 'pdp-product-gallery' && typeof window.pdpGallerySetActiveIndex === 'function') {
        emblaApi.on('select', () => {
          window.pdpGallerySetActiveIndex(emblaApi.selectedScrollSnap(), { fromEmbla: true })
        })
        window.pdpGallerySetActiveIndex(emblaApi.selectedScrollSnap(), { fromEmbla: true })
      }
    }

    const destroyEmbla = () => {
      if (!emblaApi) {
        return
      }

      emblaApi.destroy()
      emblaApi = null
      carousel._emblaApi = null

      if (carousel.id && window.emblaApis[carousel.id]) {
        delete window.emblaApis[carousel.id]
      }

      if (carousel.hasAttribute('data-carousel-dots')) {
        const dotsContainer = carousel.querySelector('[data-carousel-dots]')
        if (dotsContainer) {
          dotsContainer.innerHTML = ''
        }
      }
    }

    const evaluateMatch = () => {
      if (matchQuery && !window.matchMedia(matchQuery).matches) {
        destroyEmbla()
        return
      }

      setupEmbla()
    }

    if (matchQuery) {
      mediaQueryList = window.matchMedia(matchQuery)
      const onChange = () => evaluateMatch()

      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener('change', onChange)
      } else {
        mediaQueryList.addListener(onChange)
      }

      evaluateMatch()
    } else {
      setupEmbla()
    }
  }
}

export { Carousel }
